import React from 'react';
import { Checkbox } from '@mui/material';
import moment from 'moment';
import {
    formatAMPMNeW,
    formatHours,
    Images,
    makeCptServiceOptionId,
    PermissionList,
    renderStaffName,
    RolePermission,
    sessionStatusEnums,
    staffStatusEnums,
} from 'utils';
import { LinkRow, NonBillable, SimpleTooltip, TextRow } from 'components';
import {
    billStatuses,
    BREAK,
    DRIVE,
    isUnassignedAppt,
    PAID,
    SERVICE,
    SICK_TIME,
    STAFF,
    UNASSIGNED_STATUS,
    UNAVAILABLE,
    UNPAID,
} from '../constants';
import { ApptActionsWithModals } from './apptActionsWithModals';
import { apptDetailStatus, UnassignedStaffChip } from './modals/apptDetail/constants';
import { ApptStateInfo } from './modals/apptDetail/apptStateInfo';

import { Eligibility } from './common';
import {renderClientName} from "../../../utils";

export function getCurrentText(type) {
    switch (type) {
        case DRIVE:
            return { cardText: 'Drive', detailText: 'Drive' };
        case PAID:
            return { cardText: 'Paid', detailText: 'Paid' };
        case BREAK:
            return { cardText: 'Break', detailText: 'Break' };
        case SERVICE:
            return { cardText: 'Service', detailText: 'Service' };
        case STAFF:
            return { cardText: 'Staff', detailText: 'Staff' };
        case UNPAID:
            return { cardText: 'Unpaid', detailText: 'Unpaid' };
        case SICK_TIME:
            return { cardText: 'Sick', detailText: 'Sick' };
        case UNAVAILABLE:
            return { cardText: 'Unavailable', detailText: 'Unavailable' };
        default:
            return { cardText: '', detailText: '' };
    }
}

export const apptEnums = {
    PENDING: 'PENDING',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
};

const apptStripes = (stripe) => `repeating-linear-gradient(-45deg, #FFFFFF 0 7.5px, ${stripe} 7.5px 15.5px)`;

const apptColorThemes = {
    [SERVICE]: {
        [apptEnums.PENDING]: { color: '#35AD77', background: '#E6F5EE', border: '#B1DFCA', textColor: '#02542F' },
        [apptEnums.COMPLETED]: { color: '#02542F', background: '#B1DFCA', border: '#8BD0B1', textColor: '#02542F' },
        [apptEnums.CANCELLED]: { color: '#9CA3AF', background: apptStripes('#E6F5EE'), border: '#E6F5EE', textColor: '#026C3C' },
    },
    [DRIVE]: {
        [apptEnums.PENDING]: { color: '#F59E0B', background: '#FFFBEB', border: '#FDE68A', textColor: '#793902' },
        [apptEnums.COMPLETED]: { color: '#793902', background: '#FFF1BA', border: '#FFF1BA', textColor: '#793902' },
        [apptEnums.CANCELLED]: { color: '#9CA3AF', background: apptStripes('#FCF0E6'), border: '#FCF0E6', textColor: '#9C4A02' },
    },
    [STAFF]: {
        [apptEnums.PENDING]: { color: '#3D85E9', background: '#F5F9FE', border: '#BFDBFE', textColor: '#07387D' },
        [apptEnums.COMPLETED]: { color: '#07387D', background: '#B4D0F6', border: '#B4D0F6', textColor: '#07387D' },
        [apptEnums.CANCELLED]: { color: '#9CA3AF', background: apptStripes('#E7F0FC'), border: '#E7F0FC', textColor: '#07387D' },
    },
    [UNAVAILABLE]: {
        [apptEnums.PENDING]: { color: '#9CA3AF', background: '#FAFAFA', border: '#D1D5DB', textColor: '#1D2939' },
        [apptEnums.COMPLETED]: { color: '#1D2939', background: '#D3D3D3', border: '#D3D3D3', textColor: '#1D2939' },
        [apptEnums.CANCELLED]: { color: '#9CA3AF', background: apptStripes('#E4E7EC'), border: '#E4E7EC', textColor: '#1D2939' },
    },
    // Not in the Figma theme: a Service appt with issues keeps its red warning in the new card layout.
    ERROR: { color: '#B42318', background: '#FEF3F2', border: '#FECDCA', textColor: '#B42318' },
};

export function getBorderColorAndText(status, type, issues, unassigned) {
    const text =
        { [apptEnums.PENDING]: 'Pending', [apptEnums.COMPLETED]: 'Completed', [apptEnums.CANCELLED]: 'Cancelled' }[status] || '';
    // An unassigned Service appt keeps the normal Service colors (Figma); its orange "Unassigned" badge marks it.
    if (type === SERVICE && !unassigned && issues?.length > 0 && status !== apptEnums.CANCELLED) {
        return { ...apptColorThemes.ERROR, text };
    }
    // Staff, Break, Paid, Unpaid and Sick time share the blue family.
    const family = apptColorThemes[type] || apptColorThemes[STAFF];
    // Unavailable time has no real lifecycle; anything that isn't completed/cancelled renders as pending.
    const theme = family[status] || (type === UNAVAILABLE ? family[apptEnums.PENDING] : null);
    return theme ? { ...theme, text } : { color: '', text: '' };
}

export const apptBillingEnums = {
    BILLED: 'BILLED',
    NOT_BILLED: 'NOT_BILLED',
    CANCELLED: 'CANCELLED',
};

export const scheduleStatuses = [
    // { id: 'All', name: 'All' },
    // { id: 'RENDERED', name: 'Rendered' },
    { id: apptEnums?.COMPLETED, name: 'Completed' },
    // { id: 'NOT_RENDERED', name: 'Not Rendered' },
    { id: apptEnums?.PENDING, name: 'Pending' },
    { id: apptEnums?.CANCELLED, name: 'Cancelled' },
];
export const scheduleStatusEnums = [
    // { id: 'RENDERED', name: 'Rendered' },
    { id: apptEnums?.COMPLETED, name: 'Completed' },
    // { id: 'NOT_RENDERED', name: 'Not Rendered' },
    { id: apptEnums?.PENDING, name: 'Pending' },
    { id: UNASSIGNED_STATUS, name: 'Unassigned' },
    { id: apptEnums?.CANCELLED, name: 'Cancelled' },
];

export const scheduleBillingStatuses = [
    { id: 'All', name: 'All' },
    { id: 'BILLED', name: 'Billed' },
    { id: 'NOT_BILLED', name: 'Not Billed' },
];

export const scheduleTypes = [
    // { id: 'All', name: 'All' },
    { id: SERVICE, name: 'Service' },
    { id: STAFF, name: 'Staff' },
    { id: DRIVE, name: 'Drive' },
    { id: UNAVAILABLE, name: 'Unavailable' },
    // { id: BREAK, name: 'Break' },
    // { id: PAID, name: 'Paid' },
    // { id: UNPAID, name: 'Unpaid' },
    // { id: SICK_TIME, name: 'Sick' },
];

export const apptNotYet = {
    title: 'No appointments yet',
    subTitle: 'It looks like there are no appointments on your schedule. Start by adding new appointments.',
    image: Images.noAppt,
};

export const apptHead = (handleSelect, fullList, selected, selectAllList, globalServices) => {
    const canViewEnrollment = RolePermission([
        PermissionList.CLIENT_ENROLLMENT_READ?.code,
        PermissionList.CLIENT_ENROLLMENT_MANAGE?.code,
    ]);
    return [
        // RolePermission([PermissionList.CLAIMS_UPDATE?.code]) &&
        {
            renderView: (
                <SimpleTooltip title={<p>Select All</p>} placement="top-start">
                    <Checkbox
                        onClick={(e) => handleSelect(e, '', 'all')}
                        name={'checkBox'}
                        style={{ padding: 0, color: '#347AF0' }}
                        checked={selectAllList ? true : fullList?.length === selected?.length}
                    />
                </SimpleTooltip>
            ),
            width: '50px',
        },
        { name: 'displayId', searchKey: 'DISPLAY_ID', title: 'ID', custom: false, noSearch: true },
        {
            name: 'startDate',
            title: 'Date/Time',
            searchKey: 'createdAt',
            iconType: 'date',
            icon: 'date',
            disabled: true,
            width: '120px',
            noSearch: true,
        },
        { name: 'units', title: 'Units', custom: false, noSearch: true },
        { name: 'staff', title: 'Staff', noSearch: true },
        { name: 'client', title: 'Client', noSearch: true },
        canViewEnrollment && { name: 'payer', title: 'Payer', custom: false, noSearch: true },
        {
            name: 'types',
            title: 'Type',
            noSearch: true,
            multiple: true,
            filterList: [{ name: 'All', id: 'All' }, ...scheduleTypes],
        },

        {
            // name: 'systemServiceType',
            name: 'cptCode',
            title: 'Service',
            noSearch: true,
            multiple: true,
            returnAllValues: true,
            listWidth: 300,
            // filterList: globalServices?.length ? [{ name: 'All', id: 'All' }, ...globalServices] : [{ name: 'All', id: 'All' }],
            filterList: globalServices?.length
                ? [
                      { name: 'All', id: 'All' },
                      ...globalServices.map((i) => ({
                          name: `${i?.serviceTypeName} (${i?.cptCode})`,
                          // Encode both cptCode and serviceTypeId so the backend can pair them
                          id: makeCptServiceOptionId(i?.cptCode, i?.serviceTypeId),
                      })),
                  ]
                : [{ name: 'All', id: 'All' }],
        },
        // { name: 'service', title: 'Service', custom: false, noSearch: true },
        {
            name: 'statuses',
            title: 'Status',
            noSearch: true,
            multiple: true,
            returnAllValues: true,
            filterList: [{ name: 'All', id: 'All' }, ...scheduleStatusEnums],
        },
        { name: '', title: 'Actions', custom: false, disabled: true, width: '110px', noSearch: true },
    ];
};

export const apptBody = (handleSelect, fullList, selected, selectAllList) => {
    const canViewEnrollment = RolePermission([
        PermissionList.CLIENT_ENROLLMENT_READ?.code,
        PermissionList.CLIENT_ENROLLMENT_MANAGE?.code,
    ]);
    return [
        // RolePermission([PermissionList.CLAIMS_UPDATE?.code]) &&
        {
            button: (handle, item) => (
                <Checkbox
                    onClick={(e) => handleSelect(e, item)}
                    name={'checkBox'}
                    style={{
                        padding: 0,
                        color: '#347AF0',
                    }}
                    checked={selectAllList ? true : selected?.includes(item?.id)}
                />
            ),
            notClickable: true,
        },
        {
            rowText: (item) => (
                <div>
                    <div className="flex-align-center" style={{ gap: 8 }}>
                        <p style={{ whiteSpace: 'nowrap' }}>{item?.displayId}</p>
                        {item?.authBillable === false && (
                            <NonBillable noText={true} toolText="This appt is non-billable based on its authorization" />
                        )}
                        <div>{renderSessionStatus(item?.csessionStatus)}</div>
                    </div>
                    <ApptStateInfo appt={item} />
                </div>
            ),
        },
        {
            rowText: (item) => (
                <div>
                    <div className="flex-align-center" style={{ gap: 8 }}>
                        <p style={{ whiteSpace: 'nowrap' }}>{moment.utc(item?.startDate).format('MM/DD/YY, ddd')}</p>
                        {item?.isActualTimeSet && item?.scheduleDateTime && item?.status !== scheduleStatuses?.PENDING && (
                            <SimpleTooltip
                                title={
                                    <div>
                                        <p>Actual time differs from scheduled.</p>
                                        <p>{`Scheduled time: ${item?.scheduleDateTime}`}</p>
                                    </div>
                                }
                                placement="top"
                                arrow
                            >
                                <img src={Images.actualTime} alt="" />
                            </SimpleTooltip>
                        )}
                    </div>
                    <p className="schedule-time">{`${formatAMPMNeW(item?.startTime)}-${formatAMPMNeW(item?.endTime)}`}</p>
                </div>
            ),
        },
        {
            rowText: (item) => (
                <div style={{ whiteSpace: 'nowrap' }}>
                    <div className="flex-align-center" style={{ gap: 4 }}>
                        <p>{item?.units?.toFixed(2)}</p>
                        {item?.exactUnits && item?.units !== item?.exactUnits && (
                            <div className="table-units-rounding-box">
                                {item?.units > item?.exactUnits ? (
                                    <SimpleTooltip title={'Rounded Up'} placement="top" arrow>
                                        <div className="table-rounded-up">
                                            <img src={Images.arrowUp} alt="" />
                                        </div>
                                    </SimpleTooltip>
                                ) : (
                                    <SimpleTooltip title={'Rounded Down' + ''} placement="top" arrow>
                                        <div className="table-rounded-down">
                                            <img src={Images.arrowDown} alt="" />
                                        </div>
                                    </SimpleTooltip>
                                )}
                            </div>
                        )}
                    </div>
                    {item.hours && <div>{formatHours(item.hours)}</div>}
                </div>
            ),
        },
        {
            rowText: (item) => isUnassignedAppt(item) ? (
                <UnassignedStaffChip />
            ) : (
                <LinkRow
                    name={item?.staff?.fullName}
                    href={RolePermission([PermissionList.STAFF_READ.code]) ? `/staff/${item?.staff?.id}` : ''}
                    checkPermission={item?.staff?.id ? RolePermission([PermissionList.STAFF_READ.code]) : null}
                    textWidth={8}
                />
            ),
        },
        {
            rowText: (item) =>
                item?.client?.fullName ? (
                    <div>
                    <div className="flex-align-center" style={{ gap: 8 }}>
                        {canViewEnrollment && item?.client?.eligibilityCheck && (
                            <Eligibility info={item?.client} payerShortName={item?.payerShortName} payerName={item?.payerName} />
                        )}
                        <LinkRow
                            name={item?.client?.code}
                            href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${item?.client?.id}` : ''}
                            checkPermission={item?.client?.id ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
                            textWidth={8}
                        />
                    </div>
                        <LinkRow
                            name={renderClientName({...item?.client, code: null})}
                            href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${item?.client?.id}` : ''}
                            checkPermission={item?.client?.id ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
                            textWidth={8}
                        />
                    </div>
                ) : (
                    '--'
                ),
        },
        canViewEnrollment && {
            rowText: (item) => {
                const checkPermission = item?.payerId ? RolePermission([PermissionList.FS_READ?.code]) : null;
                const handleOpen = (e) => {
                    if (checkPermission) {
                        e.preventDefault();
                        e.stopPropagation();
                        window.open(`/fundingSource/${item?.payerId}`, '_blank', 'noopener,noreferrer');
                    }
                };

                const activeStyles = {
                    color: checkPermission ? '#347AF0' : '#4B5C68',
                    textDecoration: checkPermission ? 'underline' : 'none',
                    width: 'fit-content',
                    cursor: checkPermission ? 'pointer' : 'default',
                };

                return item?.payerName ? (
                    <SimpleTooltip title={<p className="show-tool-text-style">{item?.payerName}</p>} placement="top-start">
                        <p onClick={(e) => handleOpen(e)} style={activeStyles}>
                            {item?.payerShortName ? item?.payerShortName : `${item?.payerName?.slice(0, 5)}...`}
                        </p>
                    </SimpleTooltip>
                ) : (
                    '--'
                );
            },
        },

        {
            rowText: (item) => (
                <div>
                    <div className='flex-align-center' style={{gap: 4}}>
                    <p style={{ color: '#172B4D' }}>{getCurrentText(item?.type)?.detailText}</p>
                        {item?.supervisee &&
                            <SimpleTooltip
                                title={
                                    <div>
                                        <span>Supervised </span>
                                        <span style={{fontWeight: 600}}>{renderStaffName(item?.supervisee)}</span>
                                    </div>
                                }
                                placement="top-end"
                            >
                                <div className="supervisor-eye">
                                    <img src={Images.supervizeEye} alt="supervizeEye" />
                                </div>
                            </SimpleTooltip>
                        }
                    </div>
                    {item?.subject && <TextRow color={'#475467'} name={item?.subject} wrapperStyle={{ maxWidth: 180 }} />}
                </div>
            ),
        },

        {
            rowText: (item) => (
                <TextRow
                    name={item?.cptCode ? `${item?.cptCode} ${item?.chargeRateName ? `(${item?.chargeRateName})` : ''}` : '--'} />
            ),
        },
        {
            rowText: (item) => <div>{apptDetailStatus(item?.status, item, true)}</div>,
        },
        {
            rowText: (item) => <ApptActionsWithModals fromTable={true} item={item} afterSuccess={() => {
            }} />,
            notClickable: true,
        },
    ];
};

export const checkEditPermission = (item, userInfo) => {
    let hasPermission = false;

    const isUserActive = true;
    // const isUserActive = userInfo?.status === staffStatusEnums?.ACTIVE;
    const isNotBilled = item?.billStatus !== billStatuses?.BILLED && item?.billStatus !== billStatuses?.ON_CLAIM;

    // Editing an unassigned appt (incl. assigning its staff) needs APPT_UNASSIGNED_MANAGE — the API enforces it too.
    if (isUnassignedAppt(item) && !RolePermission([PermissionList.APPT_UNASSIGNED_MANAGE?.code])) return false;

    if (isUserActive && isNotBilled) {
        hasPermission =
            item?.type === SERVICE
                ? RolePermission([PermissionList.APPT_CONNECTED_UPDATE.code, PermissionList.APPT_SERVICE_CREATE?.code])
                : item?.type === STAFF
                  ? RolePermission([PermissionList.APPT_STAFF_CREATE?.code])
                  : item?.type === DRIVE
                    ? RolePermission([PermissionList.APPT_DRIVE_CREATE?.code])
                    : item?.type === UNAVAILABLE
                      ? RolePermission([PermissionList.APPT_UNAVAILABLE_CREATE?.code])
                      : false;

        if (!hasPermission) {
            return RolePermission([PermissionList.APPT_SELF_UPDATE?.code]) && userInfo?.id === item?.staff?.id;
        }

        return hasPermission;
    }

    return false;
};

// export const checkEditPermission = (item) => {
//     return item?.type === SERVICE ? RolePermission([PermissionList.APPT_SERVICE_CREATE?.code, PermissionList.APPT_CONNECTED_CREATE?.code]) :
//         item?.type === STAFF ? RolePermission([PermissionList.APPT_STAFF_CREATE?.code]) :
//             item?.type === DRIVE ? RolePermission([PermissionList.APPT_DRIVE_CREATE?.code]) :
//                 // item?.type === BREAK ? RolePermission([PermissionList.APPT_BREAK_CREATE?.code]) :
//                 //         item?.type === PAID ? RolePermission([PermissionList.APPT_PTO_CREATE?.code]) :
//                 //             item?.type === UNPAID ? RolePermission([PermissionList.APPT_UNPAID_CREATE?.code]) :
//                 //                 item?.type === SICK_TIME ? RolePermission([PermissionList.APPT_SICK_TIME_CREATE?.code]) :
//                 false;
// };

export const checkCreatePermission = (type) => {
    if (type === SERVICE) {
        if (RolePermission([PermissionList.APPT_SERVICE_CREATE?.code])) {
            return 'All';
        } else if (RolePermission([PermissionList.APPT_SELF_CREATE?.code])) {
            return 'Self';
        }
    }
    if (type === STAFF) {
        if (RolePermission([PermissionList.APPT_STAFF_CREATE?.code])) {
            return 'All';
        } else if (RolePermission([PermissionList.APPT_SELF_CREATE?.code])) {
            return 'Self';
        }
    }
    if (type === DRIVE) {
        if (RolePermission([PermissionList.APPT_DRIVE_CREATE?.code])) {
            return 'All';
        } else if (RolePermission([PermissionList.APPT_SELF_CREATE?.code])) {
            return 'Self';
        }
    }
    if (type === UNAVAILABLE) {
        if (RolePermission([PermissionList.APPT_UNAVAILABLE_CREATE?.code])) {
            return 'All';
        } else if (RolePermission([PermissionList.APPT_SELF_CREATE?.code])) {
            return 'Self';
        }
    }
};
// const renderStatus = (currentItem) => {
//     const { color: borderColor, text } = getBorderColorAndText(currentItem?.status, currentItem?.type);
//     return { color: borderColor, text };
// };

export const convertTo24HourMinutes = (time) => {
    const [timePart, ampm] = time.split(' ');
    const [hours, minutes] = timePart.split(':').map(Number);
    let convertedHours = hours;

    if (ampm === 'PM' && hours !== 12) {
        convertedHours += 12;
    } else if (ampm === 'AM' && hours === 12) {
        convertedHours = 0;
    }

    // Return the time in minutes since midnight
    return convertedHours * 60 + minutes;
};

export const convertTo24Hour = (t) => {
    return `${String(t.hour).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`;
};

export const weekdayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Parse a 24-hour time string "HH:MM" into total minutes since midnight.
 */
const parse24hToMinutes = (timeStr) => {
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + (m || 0);
};

/** Coarsest drag / resize / select granularity, in minutes — billing is done in 15-minute units. */
export const CALENDAR_SNAP_MINUTES = 15;
export const DEFAULT_CALENDAR_INTERVAL = 30;
/** react-big-calendar day layout: appts that overlap in time share the column side by side, never stack. */
export const CALENDAR_DAY_LAYOUT = 'no-overlap';

/**
 * Map the user's interval setting to react-big-calendar grid props.
 *
 * `interval` is the legacy `step` value (7.5 | 15 | 30 | 60); each labelled row spans `interval * 2` minutes
 * ("15 Minutes" = 7.5, "1 Hour" = 30, "2 Hour" = 60). react-big-calendar snaps drag/resize to `step`, so every
 * view uses a 15-minute step and keeps its row size by changing `timeslots` per group ("15 Minutes" = 1 slot).
 */
export const getCalendarGrid = (interval) => {
    const groupMinutes = (interval || DEFAULT_CALENDAR_INTERVAL) * 2;
    const step = CALENDAR_SNAP_MINUTES;
    return { step, timeslots: groupMinutes / step, groupMinutes };
};

export const findFromList = (date, availabilityData, step = 30) => {
    if (!availabilityData) return null;

    const weekDay = moment(date).day();
    const day = weekdayMap[weekDay];
    const fromAvailability = availabilityData[day];

    if (!Array.isArray(fromAvailability) || fromAvailability.length === 0) return null;

    const slotStart = date.getHours() * 60 + date.getMinutes();
    const slotEnd = slotStart + step;

    // Evaluate entries in order — later entries override earlier ones for the same slot.
    // Returns { startPercent, endPercent } for gradient fill, or null if unavailable.
    let result = null;
    for (const { from, to, available } of fromAvailability) {
        const fromMin = parse24hToMinutes(from);
        const toMin = parse24hToMinutes(to);

        // Calculate overlap between slot [slotStart, slotEnd] and entry [fromMin, toMin]
        const overlapStart = Math.max(slotStart, fromMin);
        const overlapEnd = Math.min(slotEnd, toMin);

        if (overlapStart < overlapEnd) {
            if (available) {
                result = {
                    startPercent: ((overlapStart - slotStart) / step) * 100,
                    endPercent: ((overlapEnd - slotStart) / step) * 100,
                };
            } else {
                result = null;
            }
        }
    }
    return result;
};

export const SubjectEnums = [
    { name: 'Break', id: BREAK },
    // {name: 'Drive', id : DRIVE},
    { name: 'Paid', id: PAID },
    { name: 'Sick Time', id: SICK_TIME },
    { name: 'Unpaid Time Off', id: UNPAID },
];

export const renderSessionStatus = (status) => {
    switch (status) {
        case sessionStatusEnums.IN_PROGRESS:
            return (
                <div>
                    <SimpleTooltip placement="top-start" title="Session in progress">
                        <div className="session-live-status" style={{ background: '#F0F9FF' }}>
                            <div style={{ background: '#0C66E4' }} />
                            <p style={{ color: '#0C66E4' }}>Live </p>
                        </div>
                    </SimpleTooltip>
                </div>
            );
        case sessionStatusEnums.PAUSED:
            return (
                <div>
                    <SimpleTooltip placement="top-start" title="Session in paused">
                        <div className="session-live-status" style={{ background: '#FEF3F2' }}>
                            <div style={{ background: '#F04438' }} />
                            <p style={{ color: '#B42318' }}>Paused </p>
                        </div>
                    </SimpleTooltip>
                </div>
            );

        // case sessionStatusEnums.COMPLETED:
        //     return (
        //         <div>
        //             <SimpleTooltip placement="top-start" title="In Progress">
        //                 <img src={Images.actualTime} alt="completed" />
        //             </SimpleTooltip>
        //         </div>
        //     );
        default:
            return null;
    }
};

export const ACTIVE_CLIENT_STAFF_TOOLTIP = {
    ON: 'On: the Staff and Client filters list only active staff, and clients that are Active, FBA, On Hold or Waitlist.',
    OFF: 'Off: all staff and clients are listed, including inactive ones.',
};
